import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Heart, Activity, Calculator } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './MaxHeartRateCalculatorPage/components/CalculationForm';
import ResultsOverview from './MaxHeartRateCalculatorPage/components/ResultsOverview';
import HeartRateZones from './MaxHeartRateCalculatorPage/components/HeartRateZones';
import QuickAdjustmentSidebar from './MaxHeartRateCalculatorPage/components/QuickAdjustmentSidebar';
import ComparisonSidebar from './MaxHeartRateCalculatorPage/components/ComparisonSidebar';
import TipsSidebar from './MaxHeartRateCalculatorPage/components/TipsSidebar';
import InfoContent from './MaxHeartRateCalculatorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  age: '30',
  gender: 'male', // 'male' или 'female'
  restingHeartRate: '60', // пульс в покое
  activityLevel: 'moderate', // 'sedentary', 'light', 'moderate', 'active', 'very_active'
  formula: 'tanaka', // формула расчета: 'tanaka', 'haskell', 'gellish', 'nes', 'gulati'
  includeRestingHeartRate: true, // использовать пульс покоя для формулы Карвонена
  includeFitnessAdjustment: false, // учитывать уровень физической подготовки
  fitnessLevel: 'average', // 'beginner', 'average', 'advanced', 'elite'
  sportSpecific: 'general', // 'general', 'running', 'cycling', 'swimming', 'strength'
};

// Определение формул расчета MHR
export const mhrFormulas = [
  { id: 'tanaka', name: 'Формула Танаки', description: '208 - (0.7 × возраст)', recommended: true, year: 2001 },
  { id: 'haskell', name: 'Формула Хаскелла-Фокса', description: '220 - возраст', recommended: false, year: 1970 },
  { id: 'gellish', name: 'Формула Геллиша', description: '207 - (0.7 × возраст)', recommended: true, year: 2007 },
  { id: 'nes', name: 'Формула НИИ Спорта', description: '211 - (0.64 × возраст)', recommended: false, year: 2012 },
  { id: 'gulati', name: 'Формула Гулати (для женщин)', description: '206 - (0.88 × возраст)', recommended: false, year: 2010 },
];

// Определение уровней активности
export const activityLevels = [
  { id: 'sedentary', name: 'Сидячий образ жизни', description: 'Минимальные физические нагрузки', coef: 1.0 },
  { id: 'light', name: 'Легкая активность', description: 'Легкие тренировки 1-2 раза в неделю', coef: 1.03 },
  { id: 'moderate', name: 'Умеренная активность', description: 'Тренировки 2-3 раза в неделю', coef: 1.05 },
  { id: 'active', name: 'Высокая активность', description: 'Интенсивные тренировки 3-5 раз в неделю', coef: 1.08 },
  { id: 'very_active', name: 'Очень высокая активность', description: 'Тренировки 6-7 раз в неделю', coef: 1.1 },
];

// Определение уровней подготовки
export const fitnessLevels = [
  { id: 'beginner', name: 'Начинающий', description: 'Опыт тренировок менее 3 месяцев', coef: 0.95 },
  { id: 'average', name: 'Средний', description: 'Регулярные тренировки от 3 мес до 1 года', coef: 1.0 },
  { id: 'advanced', name: 'Продвинутый', description: 'Регулярные тренировки более 1 года', coef: 1.05 },
  { id: 'elite', name: 'Профессиональный спортсмен', description: 'Профессиональная спортивная практика', coef: 1.1 },
];

// Определение специфик тренировок
export const sportTypes = [
  { id: 'general', name: 'Общая физическая активность', zoneAdjustment: { hr: 0, aerobic: 0, anaerobic: 0, lactate: 0, maximal: 0 } },
  { id: 'running', name: 'Бег', zoneAdjustment: { hr: 0, aerobic: 2, anaerobic: 2, lactate: 3, maximal: 0 } },
  { id: 'cycling', name: 'Велоспорт', zoneAdjustment: { hr: 0, aerobic: 0, anaerobic: 2, lactate: 2, maximal: 0 } },
  { id: 'swimming', name: 'Плавание', zoneAdjustment: { hr: -5, aerobic: 0, anaerobic: 0, lactate: 0, maximal: -3 } },
  { id: 'strength', name: 'Силовые тренировки', zoneAdjustment: { hr: 0, aerobic: -2, anaerobic: 2, lactate: 3, maximal: 2 } },
];

/**
 * MaxHeartRateCalculatorPage Component - Калькулятор максимального пульса и зон пульса для тренировок.
 */
const MaxHeartRateCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('heart-rate-calculator'), []);
  const pageTitle = tool?.metaTitle || 'Калькулятор максимального пульса (MHR) | Gensio';
  const metaDescription = tool?.metaDescription || 'Рассчитайте ваш максимальный пульс (MHR) по разным формулам с учетом возраста, пола и уровня подготовки. Определите персональные зоны пульса для эффективных тренировок и кардионагрузок.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedProfiles, setSavedProfiles] = useState([]);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных профилей из localStorage ---
  useEffect(() => {
    const savedProfilesData = localStorage.getItem('savedHeartRateProfiles');
    if (savedProfilesData) {
      try {
        setSavedProfiles(JSON.parse(savedProfilesData));
      } catch (e) {
        console.error("Error loading saved heart rate profiles:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'max-heart-rate':
          // Уже находимся на странице максимального пульса
          break;
        case 'training-zones':
          newState.includeRestingHeartRate = true;
          break;
        case 'fat-burning':
          newState.includeRestingHeartRate = true;
          newState.sportSpecific = 'general';
          break;
        case 'karvonen':
          newState.includeRestingHeartRate = true;
          newState.formula = 'tanaka';
          break;
        case 'endurance':
          newState.sportSpecific = 'running';
          newState.includeFitnessAdjustment = true;
          newState.fitnessLevel = 'advanced';
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setErrors({});

  }, [subToolId]);

  // Сохранение профиля в localStorage
  const saveProfile = (name) => {
    if (!results) return;

    const newSavedProfile = {
      id: Date.now().toString(),
      name: name || `Профиль ${formData.age} лет (${formData.gender === 'male' ? 'М' : 'Ж'})`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results }
    };

    const updatedSavedProfiles = [...savedProfiles, newSavedProfile];
    setSavedProfiles(updatedSavedProfiles);
    localStorage.setItem('savedHeartRateProfiles', JSON.stringify(updatedSavedProfiles));

    return newSavedProfile;
  };

  // Удаление сохраненного профиля
  const deleteSavedProfile = (id) => {
    const updatedSavedProfiles = savedProfiles.filter(profile => profile.id !== id);
    setSavedProfiles(updatedSavedProfiles);
    localStorage.setItem('savedHeartRateProfiles', JSON.stringify(updatedSavedProfiles));
  };

  // Загрузка сохраненного профиля
  const loadSavedProfile = (id) => {
    const profileToLoad = savedProfiles.find(profile => profile.id === id);
    if (profileToLoad) {
      setFormData(profileToLoad.formData);
      setResults(profileToLoad.results);
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Heart className="w-8 h-8 text-red-600 dark:text-red-400 flex-shrink-0" />
                Калькулятор максимального пульса
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                Рассчитайте ваш максимальный пульс (MHR) и определите оптимальные зоны интенсивности для эффективных тренировок с учетом возраста, пола и физической подготовки.
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              mhrFormulas={mhrFormulas}
              activityLevels={activityLevels}
              fitnessLevels={fitnessLevels}
              sportTypes={sportTypes}
              saveProfile={saveProfile}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} />
                <HeartRateZones 
                  results={results}
                  sportSpecific={formData.sportSpecific}
                />
              </>
            )}

            {/* --- Информационный контент о пульсе, тренировках и МПК --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
            />

            {/* --- Панель сравнения профилей --- */}
            <ComparisonSidebar 
              savedProfiles={savedProfiles}
              deleteSavedProfile={deleteSavedProfile}
              loadSavedProfile={loadSavedProfile}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-red-600 focus:ring-red-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-red-600 focus:ring-red-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Pulse Animation */
        .pulse-animation {
          animation: pulse 1.5s infinite;
        }
        @keyframes pulse {
          0% {
            transform: scale(0.95);
            box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7);
          }
          70% {
            transform: scale(1);
            box-shadow: 0 0 0 10px rgba(239, 68, 68, 0);
          }
          100% {
            transform: scale(0.95);
            box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
          }
        }
      `}</style>
    </div>
  );
};

export default MaxHeartRateCalculatorPage;